/**
 * The renderer every blueprint app shares.
 *
 * Mounted once per tail app in the router with a different `appId`. Each still
 * gets its own route family, module, flag and entitlement (build contract
 * §2.6) — they are genuinely separate products commercially. They just happen
 * to be the same twenty lines of routing.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { getAppDefinition } from '../registry';
import { RequireAppAccess } from '../RequireAppAccess';
import { getBlueprint } from './catalog';
import SetupScreen from './SetupScreen';
import RunScreen from './RunScreen';

export default function BlueprintApp({ appId }: { appId: string }) {
  const app = getAppDefinition(appId);
  const blueprint = getBlueprint(appId);
  // Both are asserted by appRegistry.test.ts; bail rather than crash if a
  // future edit registers one without the other.
  if (!app || !blueprint) return <Navigate to="/apps" replace />;

  return (
    <RequireAppAccess app={app}>
      <Routes>
        {/* Keyed so moving straight from one blueprint app to another starts a
            fresh setup — Hire's suggested criteria must not carry into Vendor
            Select. The router reuses this component across those routes. */}
        <Route index element={<SetupScreen key={blueprint.appId} blueprint={blueprint} />} />
        <Route path=":sessionId" element={<RunScreen blueprint={blueprint} />} />
        <Route path="*" element={<Navigate to={app.routeFamily} replace />} />
      </Routes>
    </RequireAppAccess>
  );
}
